/**
 * Die zwölf Profilbilder.
 *
 * Kein Upload — bewusst: kein Zuschneiden, keine fremden Dateien auf dem Server,
 * keine anstößigen Bilder, und es sieht immer gut aus. Gezeichnet in derselben
 * Bildsprache wie die Produktbilder.
 *
 * Die Kennung ("kurve-orange") steht am Konto. Wer die Liste hier ändert, muss sie
 * auch in backend/aether_auth/routes.py (AVATARE) und in
 * ~/Projects/GraphFlow-Installer/fenster/avatare.js ändern — sonst weist der
 * Server ein Bild ab, das die Oberfläche anbietet.
 */
import React from 'react';

export const AVATARE = [
  { id: 'kurve-orange',    grund: '#2A1206', farbe: '#F97316', zeichen: 'kurve' },
  { id: 'welle-bernstein', grund: '#2E2107', farbe: '#FBBF24', zeichen: 'welle' },
  { id: 'zacke-rot',       grund: '#2E0F0F', farbe: '#F87171', zeichen: 'zacke' },
  { id: 'streifen-cyan',   grund: '#06262E', farbe: '#22D3EE', zeichen: 'streifen' },
  { id: 'blende-violett',  grund: '#1E1633', farbe: '#A78BFA', zeichen: 'blende' },
  { id: 'zeilen-gruen',    grund: '#0C2A18', farbe: '#4ADE80', zeichen: 'zeilen' },
  { id: 'raster-blau',     grund: '#0E1E33', farbe: '#60A5FA', zeichen: 'raster' },
  { id: 'ring-pink',       grund: '#2E0F22', farbe: '#F472B6', zeichen: 'ring' },
  { id: 'dreieck-limette', grund: '#1B2A08', farbe: '#A3E635', zeichen: 'dreieck' },
  { id: 'quadrat-grau',    grund: '#1C1917', farbe: '#E7E5E4', zeichen: 'quadrat' },
  { id: 'kreis-tuerkis',   grund: '#06282A', farbe: '#2DD4BF', zeichen: 'kreis' },

  // Und die schlichten: eine Person, sechsmal in anderer Farbe. Nicht jeder
  // will als Kurve oder Raster auftreten — die doppelte Kurve, die frueher
  // hier stand, war fuer niemanden zu erkennen.
  { id: 'person-orange',   grund: '#2A1206', farbe: '#F97316', zeichen: 'person' },
  { id: 'person-blau',     grund: '#0E1E33', farbe: '#60A5FA', zeichen: 'person' },
  { id: 'person-gruen',    grund: '#0C2A18', farbe: '#4ADE80', zeichen: 'person' },
  { id: 'person-violett',  grund: '#1E1633', farbe: '#A78BFA', zeichen: 'person' },
  { id: 'person-pink',     grund: '#2E0F22', farbe: '#F472B6', zeichen: 'person' },
  { id: 'person-grau',     grund: '#1C1917', farbe: '#E7E5E4', zeichen: 'person' },
];

/** Alle Zeichen leben im Feld 12..36 eines 48er-Rahmens, damit sie nebeneinander
 *  gleich groß wirken. */
function zeichnung(art, c) {
  switch (art) {
    case 'kurve':
      return <path d="M13 34C20 34 19 15 26 15s7 11 9 11" fill="none" stroke={c} strokeWidth="3.4" strokeLinecap="round" />;
    case 'welle':
      return <path d="M13 24c3-11 5 9 7 0s4 7 6 1 3 5 9 0" fill="none" stroke={c} strokeWidth="3.2" strokeLinecap="round" />;
    case 'zacke':
      return <path d="M13 24l3-7 3 12 3-15 3 16 3-11 3 8 3-6 3 3" fill="none" stroke={c} strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round" />;
    case 'streifen':
      return (<>
        <rect x="12" y="15" width="24" height="4.5" rx="1" fill={c} />
        <rect x="17" y="22" width="19" height="4.5" rx="1" fill={c} opacity=".65" />
        <rect x="12" y="29" width="21" height="4.5" rx="1" fill={c} opacity=".85" />
      </>);
    case 'blende':
      return (<>
        <rect x="12" y="13" width="12" height="22" rx="1.5" fill={c} opacity=".45" />
        <rect x="24" y="13" width="12" height="22" rx="1.5" fill={c} />
      </>);
    case 'zeilen':
      return (<>
        <rect x="12" y="15" width="24" height="4" rx="2" fill={c} />
        <rect x="12" y="22" width="15" height="4" rx="2" fill={c} opacity=".6" />
        <rect x="12" y="29" width="20" height="4" rx="2" fill={c} opacity=".8" />
      </>);
    case 'raster':
      return (<>
        <rect x="12" y="13" width="10" height="10" rx="1.5" fill={c} />
        <rect x="26" y="13" width="10" height="10" rx="1.5" fill={c} opacity=".45" />
        <rect x="12" y="27" width="10" height="10" rx="1.5" fill={c} opacity=".45" />
        <rect x="26" y="27" width="10" height="10" rx="1.5" fill={c} />
      </>);
    case 'ring':
      return (<>
        <circle cx="24" cy="24" r="11" fill="none" stroke={c} strokeWidth="3.2" />
        <circle cx="24" cy="13" r="3.4" fill={c} />
      </>);
    case 'dreieck':
      return <path d="M24 13l11 21H13z" fill={c} />;
    case 'quadrat':
      return <rect x="13" y="13" width="22" height="22" rx="3" fill="none" stroke={c} strokeWidth="3.4" />;
    case 'kreis':
      return <circle cx="24" cy="24" r="11" fill={c} />;
    case 'person':
    default:
      // Kopf und Schultern, mittig im selben Feld wie die anderen Zeichen.
      // Auch der Rueckfall: ein unbekanntes Zeichen zeigt lieber eine Person
      // als ein Gekritzel.
      return (<>
        <circle cx="24" cy="19" r="6.5" fill={c} />
        <path d="M12.5 36c0-6.4 5.1-11 11.5-11s11.5 4.6 11.5 11z" fill={c} />
      </>);
  }
}

export function Avatar({ id, size = 40, className = '' }) {
  const a = AVATARE.find(x => x.id === id) || AVATARE[0];
  return (
    <svg viewBox="0 0 48 48" width={size} height={size} className={className} aria-hidden="true">
      <rect width="48" height="48" rx="24" fill={a.grund} />
      {zeichnung(a.zeichen, a.farbe)}
    </svg>
  );
}
